import { difference, filter, isEmpty } from 'lodash'
import { horizontalGutter, verticalGutter } from 'global/styles/global'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import countries from 'constants/countries'
import { differenceInDays } from 'date-fns'
import paths from 'constants/paths'
import propTypes from 'global/propTypes'
import PropTypes from 'prop-types'
import { Row } from 'antd'
import { SHOW_FEEDBACK_WIDGET } from 'global/featureFlags'
import usePreviousState from 'hooks/usePreviousState'

import CommentsTab from './components/CommentsTab'
import FeedbackModal from './components/FeedbackModal'
import feedbackQuestions from './components/FeedbackModal/feedbackQuestions'
import General from './components/General/General'
import GiveFeedbackWidget from './components/GiveFeedbackWidget'
import PickFeedbackQuestionModal from './components/PickFeedbackQuestionModal'
import Productivity from './components/Productivity'
import ShipOverviewTab from './components/ShipOverviewTab'
import SummaryTab from './components/SummaryTab'
import Tabs from './components/Tabs'

// Limits age of closed port calls to ask for feedback
const FEEDBACK_CUT_OFF_DAYS = 30

const PortCall = ({
  answeredFeedbackIds,
  baplieRestowHistory,
  baplieRestowHistoryError,
  baplieRestowDownloadError,
  baplieUploadError,
  comments,
  containerMovesProgress,
  containerNotes,
  departureAndCompletionTime,
  downloadPortCallReport,
  downloadTerminalPerformanceReport,
  draftSurvey,
  draftSurveySaveError,
  gantryMovesSummary,
  gearboxLocations,
  general,
  handlePostComment,
  handleSendFeedback,
  isPostingComment,
  isRequestingBaplieRestowHistory,
  isRequestingComments,
  isRequestingContainerMovesProgress,
  isRequestingDepartureAndCompletionTime,
  isRequestingDraftSurvey,
  isRequestingGantryMovesSummary,
  isRequestingGearboxLocations,
  isRequestingGeneral,
  isRequestingLogbookRemarks,
  isRequestingContainerNotes,
  isRequestingMilestones,
  isRequestingReportsSummary,
  isRequestingShipOverview,
  isDownloadingBaplieRestow,
  isUploadingBaplieRestow,
  isSavingDraftSurvey,
  logbookRemarks,
  milestones,
  numberOfComments,
  numberOfContainerNotes,
  numberOfLogbookRemarks,
  onDownloadBaplieRestow,
  onUploadBaplieRestow,
  onSavePortCallDraftSurvey,
  onUnlockDeviceBay,
  portCallBaplieRestowUploadFailed,
  productivity,
  reportsSummary,
  setSavePortCallDraftSurveyError,
  setUnlockDeviceBayError,
  shipOverview,
  shouldShowGeneralLoadingSkeleton,
  shouldShowProductivityLoading,
  shouldShowShipOverviewTabLoadingSkeleton,
  shouldShowSummaryTabLoadingSkeleton,
  shouldShowTabsLoading,
  tab,
  unlockDeviceBayError,
  unlockingDeviceId,
  userRole,
}) => {
  const location = useLocation()
  const navigate = useNavigate()

  if (!tab) {
    navigate(`${location.pathname}/${paths.portCall.summary}`, {
      replace: true,
    })
  }

  const isCountryUnknown = general
    ? general.portCountry === countries.NOT_KNOWN
    : false

  const feedbackQuestionsForUser = filter(feedbackQuestions, question =>
    question.userRoles.includes(userRole)
  )

  const feedbackIds = feedbackQuestionsForUser.map(question => question.id)
  const unansweredFeedbackIds = difference(feedbackIds, answeredFeedbackIds)

  const hasFeedbackToAsk = !isEmpty(unansweredFeedbackIds)

  const isPortCallOpen = general ? !general.isPortCallFinished : false

  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const closeFeedbackModal = () => setShowFeedbackModal(false)

  const wasFeedbackModalOpen = usePreviousState(showFeedbackModal)

  // Manage FeedbackModal visibility
  useEffect(() => {
    if (
      (!isRequestingGeneral && isPortCallOpen) ||
      !general ||
      isRequestingGeneral
    ) {
      setShowFeedbackModal(false)

      return
    }

    const daysSincePortCallClosed = differenceInDays(
      new Date(),
      new Date(general.portCallClosed)
    )

    if (daysSincePortCallClosed >= FEEDBACK_CUT_OFF_DAYS) {
      setShowFeedbackModal(false)

      return
    }

    // Prevents FeedbackModal closing after submitting feedback
    if (wasFeedbackModalOpen) {
      return
    }

    setShowFeedbackModal(hasFeedbackToAsk)
  }, [
    general,
    hasFeedbackToAsk,
    isPortCallOpen,
    isRequestingGeneral,
    wasFeedbackModalOpen,
  ])

  const [showPickFeedbackQuestionModal, setShowPickFeedbackQuestionModal] =
    useState(false)
  const openPickFeedbackQuestionModal = () =>
    setShowPickFeedbackQuestionModal(true)
  const closePickFeedbackQuestionModal = () =>
    setShowPickFeedbackQuestionModal(false)

  const [selectedFeedbackQuestionId, setSelectedFeedbackQuestionId] =
    useState(null)

  const onQuestionClicked = question => {
    setSelectedFeedbackQuestionId(question.id)
    setShowFeedbackModal(true)
    closePickFeedbackQuestionModal()
  }

  return (
    <div className="page__container">
      <Row gutter={[horizontalGutter, verticalGutter]} type="flex">
        <General
          departureAndCompletionTime={departureAndCompletionTime}
          general={general}
          isCountryUnknown={isCountryUnknown}
          isRequestingGeneral={isRequestingGeneral}
          shouldShowGeneralLoadingSkeleton={shouldShowGeneralLoadingSkeleton}
          isRequestingDepartureAndCompletionTime={
            isRequestingDepartureAndCompletionTime
          }
        />

        <Tabs
          isLoading={shouldShowTabsLoading}
          noOfComments={numberOfComments}
          noOfContainerNotes={numberOfContainerNotes}
          noOfRemarks={numberOfLogbookRemarks}
        />

        {tab === paths.portCall.summary && (
          <SummaryTab
            containerMovesProgress={containerMovesProgress}
            downloadPortCallReport={downloadPortCallReport}
            gantryMovesSummary={gantryMovesSummary}
            isCountryUnknown={isCountryUnknown}
            isRequestingGantryMovesSummary={isRequestingGantryMovesSummary}
            isRequestingMilestones={isRequestingMilestones}
            isRequestingReportsSummary={isRequestingReportsSummary}
            milestones={milestones}
            portCallStatus={general ? general.portCallStatus : null}
            reportsSummary={reportsSummary}
            shouldShowLoadingSkeletons={shouldShowSummaryTabLoadingSkeleton}
            downloadTerminalPerformanceReport={
              downloadTerminalPerformanceReport
            }
            isRequestingContainerMovesProgress={
              isRequestingContainerMovesProgress
            }
          />
        )}

        {tab === paths.portCall.productivity && (
          <Productivity
            isCountryUnknown={isCountryUnknown}
            isLoading={shouldShowProductivityLoading}
            isPortCallOpen={isPortCallOpen}
            portCallClosed={general ? general.portCallClosed : null}
            productivity={productivity}
          />
        )}

        {tab === paths.portCall.shipOverview && (
          <ShipOverviewTab
            baplieRestowDownloadError={baplieRestowDownloadError}
            baplieRestowHistory={baplieRestowHistory}
            baplieRestowHistoryError={baplieRestowHistoryError}
            baplieUploadError={baplieUploadError}
            draftSurvey={draftSurvey}
            draftSurveySaveError={draftSurveySaveError}
            gearboxLocations={gearboxLocations}
            isBaplieRestowHistoryLoading={isRequestingBaplieRestowHistory}
            isDownloadingBaplieRestow={isDownloadingBaplieRestow}
            isDraftSurveyLoading={isRequestingDraftSurvey}
            isGearboxLocationsLoading={isRequestingGearboxLocations}
            isPortCallClosed={general ? general.isPortCallFinished : false}
            isSavingDraftSurvey={isSavingDraftSurvey}
            isShipOverviewLoading={isRequestingShipOverview}
            isUploadingBaplieRestow={isUploadingBaplieRestow}
            onDownloadBaplieRestow={onDownloadBaplieRestow}
            onSavePortCallDraftSurvey={onSavePortCallDraftSurvey}
            onUnlockDeviceBay={onUnlockDeviceBay}
            onUploadBaplieRestow={onUploadBaplieRestow}
            portCallBaplieRestowUploadFailed={portCallBaplieRestowUploadFailed}
            setSavePortCallDraftSurveyError={setSavePortCallDraftSurveyError}
            setUnlockDeviceBayError={setUnlockDeviceBayError}
            shipOverview={shipOverview}
            shouldShowLoadingSkeleton={shouldShowShipOverviewTabLoadingSkeleton}
            unlockDeviceBayError={unlockDeviceBayError}
            unlockingDeviceId={unlockingDeviceId}
            userRole={userRole}
          />
        )}

        {tab === paths.portCall.comments && (
          <CommentsTab
            comments={comments}
            containerNotes={containerNotes}
            handlePostComment={handlePostComment}
            isCountryUnknown={isCountryUnknown}
            isLoadingContainerNotes={isRequestingContainerNotes}
            isLoadingLogbook={isRequestingLogbookRemarks}
            isPostingComment={isPostingComment}
            isRequestingComments={isRequestingComments}
            logbookRemarks={logbookRemarks}
            noOfComments={numberOfComments}
            noOfContainerNotes={numberOfContainerNotes}
            noOfRemarks={numberOfLogbookRemarks}
          />
        )}
      </Row>

      {SHOW_FEEDBACK_WIDGET && showFeedbackModal && (
        <FeedbackModal
          feedbackQuestionsForUser={feedbackQuestionsForUser}
          onClose={closeFeedbackModal}
          onSubmit={handleSendFeedback}
          userRole={userRole}
          questionIds={
            selectedFeedbackQuestionId
              ? [selectedFeedbackQuestionId]
              : unansweredFeedbackIds
          }
        />
      )}

      {SHOW_FEEDBACK_WIDGET &&
        !showPickFeedbackQuestionModal &&
        !isRequestingGeneral &&
        !showFeedbackModal && (
          <GiveFeedbackWidget onClick={openPickFeedbackQuestionModal} />
        )}

      {SHOW_FEEDBACK_WIDGET && showPickFeedbackQuestionModal && (
        <PickFeedbackQuestionModal
          feedbackQuestionsForUser={feedbackQuestionsForUser}
          onClose={closePickFeedbackQuestionModal}
          onQuestionClicked={onQuestionClicked}
        />
      )}
    </div>
  )
}

PortCall.defaultProps = {
  baplieRestowHistory: [],
  comments: null,
  containerMovesProgress: null,
  draftSurvey: {
    arrivalDrafts: null,
    arrivalWaterDensity: null,
    departureDrafts: null,
    departureWaterDensity: null,
  },
  general: null,
  productivity: null,
  reportsSummary: null,
  shipOverview: null,
  unlockDeviceBayError: null,
  unlockingDeviceId: null,
}

PortCall.propTypes = {
  answeredFeedbackIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  baplieRestowDownloadError: PropTypes.string,
  baplieRestowHistory: PropTypes.array,
  baplieRestowHistoryError: PropTypes.bool.isRequired,
  baplieUploadError: PropTypes.string,
  comments: PropTypes.arrayOf(propTypes.portCallComment),
  containerMovesProgress: propTypes.containerMovesProgress,
  containerNotes: PropTypes.arrayOf(
    PropTypes.shape({
      categoryId: PropTypes.number,
      cellNumber: PropTypes.string,
      completedAt: PropTypes.string,
      containerNo: PropTypes.string,
      image: PropTypes.shape({
        fullUrl: PropTypes.string,
        thumbnailUrl: PropTypes.string,
      }),
      isComplete: PropTypes.bool,
      loggedAt: PropTypes.string.isRequired,
      remark: PropTypes.string.isRequired,
      updateAt: PropTypes.string,
    })
  ),
  departureAndCompletionTime: propTypes.departureAndCompletionTime.isRequired,
  downloadPortCallReport: PropTypes.func.isRequired,
  downloadTerminalPerformanceReport: PropTypes.func.isRequired,
  draftSurvey: propTypes.draftSurvey,
  draftSurveySaveError: PropTypes.string,
  gantryMovesSummary: propTypes.gantryMovesSummary.isRequired,
  gearboxLocations: PropTypes.arrayOf(PropTypes.string).isRequired,
  general: propTypes.portCallGeneral,
  handlePostComment: PropTypes.func.isRequired,
  handleSendFeedback: PropTypes.func.isRequired,
  isDownloadingBaplieRestow: PropTypes.bool.isRequired,
  isPostingComment: PropTypes.bool.isRequired,
  isRequestingBaplieRestowHistory: PropTypes.bool.isRequired,
  isRequestingComments: PropTypes.bool.isRequired,
  isRequestingContainerMovesProgress: PropTypes.bool.isRequired,
  isRequestingContainerNotes: PropTypes.bool.isRequired,
  isRequestingDepartureAndCompletionTime: PropTypes.bool.isRequired,
  isRequestingDraftSurvey: PropTypes.bool.isRequired,
  isRequestingGantryMovesSummary: PropTypes.bool.isRequired,
  isRequestingGearboxLocations: PropTypes.bool.isRequired,
  isRequestingGeneral: PropTypes.bool.isRequired,
  isRequestingLogbookRemarks: PropTypes.bool.isRequired,
  isRequestingMilestones: PropTypes.bool.isRequired,
  isRequestingReportsSummary: PropTypes.bool.isRequired,
  isRequestingShipOverview: PropTypes.bool.isRequired,
  isSavingDraftSurvey: PropTypes.bool.isRequired,
  isUploadingBaplieRestow: PropTypes.bool.isRequired,
  logbookRemarks: propTypes.logbookRemarks.isRequired,
  milestones: propTypes.milestones.isRequired,
  numberOfComments: PropTypes.number.isRequired,
  numberOfContainerNotes: PropTypes.number.isRequired,
  numberOfLogbookRemarks: PropTypes.number.isRequired,
  onDownloadBaplieRestow: PropTypes.func.isRequired,
  onSavePortCallDraftSurvey: PropTypes.func.isRequired,
  onUnlockDeviceBay: PropTypes.func.isRequired,
  onUploadBaplieRestow: PropTypes.func.isRequired,
  portCallBaplieRestowUploadFailed: PropTypes.func.isRequired,
  productivity: propTypes.portCallProductivity,
  reportsSummary: propTypes.reports,
  setSavePortCallDraftSurveyError: PropTypes.func,
  setUnlockDeviceBayError: PropTypes.func.isRequired,
  shipOverview: propTypes.shipOverview,
  shouldShowGeneralLoadingSkeleton: PropTypes.bool.isRequired,
  shouldShowProductivityLoading: PropTypes.bool.isRequired,
  shouldShowShipOverviewTabLoadingSkeleton: PropTypes.bool.isRequired,
  shouldShowSummaryTabLoadingSkeleton: PropTypes.bool.isRequired,
  shouldShowTabsLoading: PropTypes.bool.isRequired,
  tab: PropTypes.oneOf([
    paths.portCall.comments,
    paths.portCall.summary,
    paths.portCall.shipOverview,
    paths.portCall.productivity,
  ]),
  unlockDeviceBayError: PropTypes.string,
  unlockingDeviceId: PropTypes.string,
  userRole: propTypes.userRole.isRequired,
}

export default PortCall
